import { useMemo } from "react";
import { useGetPersonalTierListByChampRoleQuery, useGetPlayerMatchupStatsByChampRoleQuery } from "@ugitgud/legos/queries/lol";
import { useRsoConnection } from "@ugg/shared/api/requests/accounts/oauth-hooks";
import { getTier } from "@ugg/shared/utils/tier-helpers";
import type { PersonalizedBuildStats } from "@ugg/shared/components/Champions/RecommendedBuild/PersonalizedBuildCard";
import type { PersonalizedMatchup } from "@ugg/shared/components/Champions/pages/Overview/ToughestMatchups";

interface SummonerIdentity {
  summonerName: string;
  riotTagLine: string;
  displayTagLine: string;
  iconId: number;
  regionId: string;
}

const useSummonerIdentity = (): SummonerIdentity | null => {
  const { summoner } = useRsoConnection();
  if (!summoner?.riotUserName || !summoner?.riotTagLine || !summoner?.regionId) {
    return null;
  }
  return {
    summonerName: summoner.riotUserName,
    riotTagLine: summoner.riotTagLine,
    displayTagLine: `#${summoner.riotTagLine}`,
    iconId: Number(summoner.iconId) || 0,
    regionId: summoner.regionId,
  };
};

const buildEmptyStats = (summoner: SummonerIdentity): PersonalizedBuildStats => ({
  summonerName: summoner.summonerName,
  tagLine: summoner.displayTagLine,
  region: summoner.regionId,
  iconId: summoner.iconId,
  lp: 0,
  games: 0,
  tier: "-",
  winRate: 0,
  pickRate: 0,
  kda: 0,
});

interface PersonalizedHookOptions {
  championId: number;
  role: number;
  queueType: number;
  skip?: boolean;
}

interface PersonalizedBuildResult {
  data: PersonalizedBuildStats | null;
  summonerName: string | undefined;
  tagLine: string | undefined;
  loading: boolean;
}

export const usePersonalizedBuildStats = ({
  championId,
  role,
  queueType,
  skip,
}: PersonalizedHookOptions): PersonalizedBuildResult => {
  const summoner = useSummonerIdentity();

  const shouldQuery = !skip && summoner !== null && queueType > 0 && championId > 0;

  const { data, loading } = useGetPersonalTierListByChampRoleQuery({
    variables: {
      championId,
      queueIds: queueType,
      regionId: summoner?.regionId ?? "",
      riotTagLine: summoner?.riotTagLine ?? "",
      riotUserName: summoner?.summonerName ?? "",
      role,
    },
    skip: !shouldQuery,
  });

  const stats = useMemo<PersonalizedBuildStats | null>(() => {
    if (!summoner) return null;
    if (loading) return null;

    const emptyStats = buildEmptyStats(summoner);
    const championRow = data?.getPersonalTierListByChampRole?.[0];
    if (!championRow) return emptyStats;

    const totalMatches = championRow.totalMatches ?? 0;
    if (totalMatches <= 0) return emptyStats;

    const wins = championRow.wins ?? 0;
    const kills = championRow.kills ?? 0;
    const deaths = championRow.deaths ?? 0;
    const assists = championRow.assists ?? 0;

    const kda = deaths === 0 ? kills + assists : (kills + assists) / deaths;
    const winRate = Math.round((wins / totalMatches) * 1000) / 10;
    const pickRate = Math.round((championRow.pickRate ?? 0) * 1000) / 10;

    return {
      summonerName: summoner.summonerName,
      tagLine: summoner.displayTagLine,
      region: summoner.regionId,
      iconId: summoner.iconId,
      lp: championRow.lpAvg ?? 0,
      games: totalMatches,
      tier: championRow.tier ?? getTier(winRate),
      winRate,
      pickRate,
      kda: Number.isFinite(kda) ? kda : 0,
    };
  }, [data, championId, summoner, loading]);

  return {
    data: stats,
    summonerName: summoner?.summonerName,
    tagLine: summoner?.displayTagLine,
    loading,
  };
};

interface PersonalizedMatchupsResult {
  data: PersonalizedMatchup[];
  summonerName: string | undefined;
  tagLine: string | undefined;
  loading: boolean;
}

export const usePersonalizedMatchups = ({
  championId,
  role,
  queueType,
  skip,
  count = 3,
}: PersonalizedHookOptions & { count?: number }): PersonalizedMatchupsResult => {
  const summoner = useSummonerIdentity();

  const shouldQuery = !skip && summoner !== null && queueType > 0 && championId > 0;

  const { data, loading } = useGetPlayerMatchupStatsByChampRoleQuery({
    variables: {
      championId,
      queueType,
      regionId: summoner?.regionId ?? "",
      riotTagLine: summoner?.riotTagLine ?? "",
      riotUserName: summoner?.summonerName ?? "",
      role,
    },
    skip: !shouldQuery,
  });

  const matchups = useMemo<PersonalizedMatchup[]>(() => {
    const rows = data?.getPlayerMatchupStatsByChampRole;
    const championRow = rows?.[0];
    const matchupStats = championRow?.matchupStats ?? [];

    return matchupStats
      .filter((m): m is NonNullable<typeof m> => !!m && (m.totalGames ?? 0) > 0 && m.opponentChampionId != null)
      .map((m) => {
        const totalGames = m.totalGames ?? 0;
        const winSum = m.winSum ?? 0;
        const winRate = Math.round((winSum / totalGames) * 1000) / 10;
        return {
          championId: m.opponentChampionId as number,
          winRate,
          games: totalGames,
        };
      })
      .sort((a, b) => a.winRate - b.winRate || b.games - a.games)
      .slice(0, count);
  }, [data, count]);

  return {
    data: matchups,
    summonerName: summoner?.summonerName,
    tagLine: summoner?.displayTagLine,
    loading,
  };
};
